import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import {
    generateSearchParamsForCustomerList as generateSliceSearchParams,
    customersExpansionPotentialFilter as sliceExpansionPotentialParams,
    customersRevenueDeclineFilter as sliceRevenueDeclineParams,
    type CustomerListParams,
} from '@plato/app-shared/utils/customer-list';
import { SrCustomerStatus } from '@plato/data/starrocks/types';
import { AiIcon } from '@plato/ui-legacy/assets/icons/ai-icon';
import { Card } from '@plato/ui-legacy/components/ui/card';
import SmallSpinner from '@plato/ui-legacy/components/ui/small-spinner';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { Switch } from '@plato/ui-legacy/components/ui/toggle';
import { useLocalStorage } from '@plato/ui-legacy/lib/hooks/use-local-storage';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { GetManyCustomersFilters } from '../../../server/types/infer-trpc-types.ts';
import { resolveActiveMetric } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { HomeRecommendationList } from '../../analytics/types/analytics-events.ts';
import {
    customersExpansionPotentialFilter,
    customersRevenueDeclineFilter,
} from '../../customer-list/constants/customer-list-filters.ts';
import { generateSearchParamsForCustomerList } from '../../customer-list/hooks/use-customer-list-filters.tsx';
import { DashboardCustomerCard } from '../components/dashboard-customer-card.tsx';
import { DashboardSection } from '../components/dashboard-section.tsx';

const STORAGE_KEY = 'home_recommendations_show_dormant';

type CustomerListProps = {
    enabled: boolean;
    filter: GetManyCustomersFilters;
    listParams: CustomerListParams;
    list: HomeRecommendationList;
    title: string;
    subtitle: string;
    showMoreText?: string;
    limit?: number;
};

function CustomerList({
    enabled,
    filter,
    listParams,
    list,
    title,
    subtitle,
    showMoreText,
    limit = 5,
}: CustomerListProps) {
    const { t } = useTranslation('dashboard');
    const sendEvent = useAnalytics();
    const appConfig = useAppConfig();
    const { enableCustomerListFeatureSlice } = appConfig;
    const { billingStatus } = resolveActiveMetric(appConfig);
    const { data: customerData, isLoading } = api.platoUser.getManyCustomers.getListWithFilters.useQuery(
        {
            ...filter,
            businessStatus: 'active',
            excludeErpStatusBlocked: true,
            billingStatus,
            limit,
        },
        {
            enabled,
        },
    );

    const { data: totalCount } = api.platoUser.getManyCustomers.getCount.useQuery(
        { ...filter, excludeErpStatusBlocked: true },
        {
            enabled,
        },
    );

    const data = customerData
        ? {
              snippetList: customerData.result,
              countLessShown: (totalCount || 0) - customerData.result.length,
          }
        : null;

    return (
        <div className="flex flex-col">
            <Card className="mb-3 p-3 border-none bg-neutral-25">
                <div className="flex flex-col">
                    <Text weight="semibold" color="neutral-900" className="text-sm">
                        {title}
                    </Text>
                    <Text color="neutral-500" className="text-xs mt-0.5">
                        {subtitle}
                    </Text>
                </div>
            </Card>

            {isLoading && (
                <div className="flex h-[100px] w-full items-center justify-center">
                    <SmallSpinner />
                </div>
            )}
            {data && !isLoading && (
                <>
                    <div className="flex flex-col gap-2">
                        {data.snippetList.map((customer, index) => (
                            <DashboardCustomerCard
                                key={customer.customerId}
                                customer={customer}
                                list={list}
                                position={index + 1}
                            />
                        ))}
                    </div>

                    {data.countLessShown > 0 && (
                        <Link
                            onClick={() => {
                                sendEvent('home_show_all_clicked', { section: list });
                            }}
                            to={{
                                pathname: '/customers',
                                search: enableCustomerListFeatureSlice
                                    ? generateSliceSearchParams(listParams)
                                    : generateSearchParamsForCustomerList(filter),
                            }}
                            className="mt-3 flex items-center justify-end gap-1 text-sm font-medium leading-[18px] text-blue-700 hover:underline"
                        >
                            <span>{showMoreText || t('analysis_section__show_all')}</span>
                            <ChevronRightIcon className="h-4 w-4" />
                        </Link>
                    )}
                </>
            )}
        </div>
    );
}

type DashboardCustomersSectionProps = {
    enabled: boolean;
};

function applyStatusFilter(filter: GetManyCustomersFilters, showDormant: boolean): GetManyCustomersFilters {
    return {
        ...filter,
        statuses: showDormant
            ? []
            : [SrCustomerStatus.ACTIVE, SrCustomerStatus.AT_RISK, SrCustomerStatus.NEW],
    };
}

function applyStatusListParams(params: CustomerListParams, showDormant: boolean): CustomerListParams {
    return { ...params, statuses: showDormant ? [] : ['active', 'at_risk', 'new'] };
}

export function DashboardCustomersSection({ enabled }: DashboardCustomersSectionProps) {
    const { t } = useTranslation('dashboard');
    const sendEvent = useAnalytics();
    const [showDormant, setShowDormant] = useLocalStorage(STORAGE_KEY, true);

    const handleToggleChange = (checked: boolean) => {
        setShowDormant(checked);
        sendEvent('home_recommendations_inactive_toggle_changed', {
            show_inactive: checked,
            source: 'home_dashboard',
        });
    };

    return (
        <DashboardSection className="flex flex-col gap-4 p-4">
            {/* AI Notice */}
            <div className="flex flex-col items-start sm:flex-row sm:justify-between gap-x-1.5 gap-y-1 rounded-md   text-sm font-normal px-3 py-1.5  bg-linear-to-r from-[#4c74e61a] to-[#8d54e91a] text-neutral-700">
                <div className="flex items-center min-h-5 space-x-1.5">
                    <AiIcon />
                    <Text weight="semibold" color="neutral-800">
                        {t('analysis_section__ai_notice', 'Recommended Customers')}
                    </Text>
                </div>
                <Switch
                    wrapperClassName="sm:flex-row-reverse"
                    size="sm"
                    checked={showDormant}
                    onCheckedChange={handleToggleChange}
                    label={t('analysis_section__show_dormant_customers')}
                />
            </div>
            {/* Customer Lists */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                    <CustomerList
                        enabled={enabled}
                        filter={applyStatusFilter(customersExpansionPotentialFilter, showDormant)}
                        listParams={applyStatusListParams(sliceExpansionPotentialParams, showDormant)}
                        list="expansion_potential"
                        title={t('analysis_section__expansion_potential_title', '🚀 Expansion Potential')}
                        subtitle={t(
                            'analysis_section__expansion_potential_subtitle',
                            'Here you can find significant opportunities to grow revenue',
                        )}
                    />
                </div>
                <div className="flex-1">
                    <CustomerList
                        enabled={enabled}
                        filter={applyStatusFilter(customersRevenueDeclineFilter, showDormant)}
                        listParams={applyStatusListParams(sliceRevenueDeclineParams, showDormant)}
                        list="revenue_decline"
                        title={t('analysis_section__revenue_decline_title', '⚠️ Revenue Decline')}
                        subtitle={t(
                            'analysis_section__revenue_decline_subtitle',
                            'These customers have churned overall or in key categories',
                        )}
                    />
                </div>
            </div>
        </DashboardSection>
    );
}
